import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SavedUser {
  id: string;
  saved_user_id: string;
  notes: string | null;
  created_at: string;
  user: {
    id: string;
    name: string | null;
    avatar_url: string | null;
    status: string | null;
    lat: number | null;
    lng: number | null;
    is_active: boolean | null;
    location_updated_at: string | null;
  } | null;
}

export const useSavedUsers = () => {
  return useQuery({
    queryKey: ["saved-users"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: savedData, error } = await supabase
        .from("saved_users")
        .select("id, saved_user_id, notes, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (!savedData || savedData.length === 0) return [];

      // Fetch user details for all saved users (uses safe_users view for security)
      const userIds = savedData.map((s) => s.saved_user_id);
      const { data: usersData, error: usersError } = await supabase
        .from("safe_users")
        .select("id, name, avatar_url, status, lat, lng, is_active, location_updated_at")
        .in("id", userIds);

      if (usersError) throw usersError;

      // Combine the data
      const result: SavedUser[] = savedData.map((saved) => ({
        ...saved,
        user: usersData?.find((u) => u.id === saved.saved_user_id) || null,
      }));

      return result;
    },
  });
};
